import { User } from '@frontegg/redux-store/auth/interfaces';
import { decodeJwtPayload } from '../util/jwt';
import { ADMIN_PERMISSION, FUND_ADMIN_PERMISSION, PermissionKey, PersonaKey } from './PermissionAndRolesKeys';

// Persona keys never take the wildcard path, whichever check they reach through
const PERSONA_PREFIX = 'fd.persona.';

function stringClaims(claim: unknown): string[] {
  return Array.isArray(claim) ? claim.filter((entry): entry is string => typeof entry === 'string') : [];
}

export class PermissionService {
  static #instance: PermissionService | undefined;

  readonly #permissions = new Set<string>();

  #init(user: User) {
    // Frontegg's user state only lists permissions held directly on the active
    // account, so anything granted through a parent-account role is missing
    // from it. The access token is what the API authorizes against, so its
    // permission claims are merged in and the UI recognizes at least whatever
    // the token carries.
    this.#permissions.clear();
    user.permissions.forEach((permission) => {
      this.#permissions.add(permission.key);
    });
    this.#addTokenClaims(user.accessToken);
  }

  #addTokenClaims(accessToken: string) {
    stringClaims(decodeJwtPayload(accessToken)?.permissions).forEach((key) => {
      this.#permissions.add(key);
    });
  }

  /**
   * The permissions the access token alone carries, for deciding what to fetch before Frontegg's user
   * object arrives. Not the app's permissions: rendering waits for {@link PermissionService.initService}.
   */
  static forAccessToken(accessToken: string): PermissionService {
    const service = new PermissionService();
    service.#addTokenClaims(accessToken);
    return service;
  }

  hasPermission(permissionKey: PermissionKey | string) {
    if (permissionKey.startsWith(PERSONA_PREFIX)) {
      return this.#permissions.has(permissionKey);
    }
    return this.#permissions.has(permissionKey) || this.#hasTopPermission(permissionKey);
  }

  /** Personas are identity, not capability, so no wildcard can grant one. */
  hasPersona(personaKey: PersonaKey) {
    return this.#permissions.has(personaKey);
  }

  isAuditor() {
    return this.hasPermission(PermissionKey.hasAuditorAccess);
  }

  /** The role itself, never an admin who also holds it. */
  isFundAdmin() {
    return this.#permissions.has(FUND_ADMIN_PERMISSION) && !this.#permissions.has(ADMIN_PERMISSION);
  }

  // Portal-only users manage accounts in Frontegg and have nothing to see in the
  // app. Someone who also holds an app role keeps the app (MAGGIE-9123); the
  // landing-page permissions decide that, not a count of roles.
  isPortalOnly() {
    return (
      this.hasPersona(PersonaKey.portalOnly) &&
      !this.hasPermission(PermissionKey.canViewMetrics) &&
      !this.hasPermission(PermissionKey.canViewList)
    );
  }

  canManageDefaultViews() {
    return this.hasPermission(PermissionKey.canViewAppAdminPanel);
  }

  #hasTopPermission(permissionKey: string): boolean {
    const permParts = permissionKey.split('.');
    let current = '';

    return permParts.some((perm, index) => {
      current = index === 0 ? perm : `${current}.${perm}`;
      return this.#permissions.has(`${current}.*`);
    });
  }

  static initService(user: User) {
    if (!PermissionService.#instance) {
      PermissionService.#instance = new PermissionService();
    }
    PermissionService.#instance!.#init(user);
  }

  static isInitialized() {
    return !!PermissionService.#instance;
  }

  static get() {
    if (!PermissionService.#instance) {
      throw new Error(`Service has not been initialized`);
    }
    return PermissionService.#instance;
  }

  static destroyService() {
    PermissionService.#instance = undefined;
  }
}
